import React, { useEffect, useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Divider,
  Typography,
  Paper,
  Box,
  Chip,
  Collapse
} from '@mui/material';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import { useMain } from 'src/views/hooks/useMain';
import CloseIcon from '@mui/icons-material/Close';

const BILL_TAG_CONFIG = {
  Bill: { label: 'Service/Product', color: 'primary' },
  Wallet: { label: 'Wallet', color: 'success' },
  Membership: { label: 'Membership', color: 'secondary' },
  Package: { label: 'Package', color: 'warning' },
};

const getBillTag = (billType) => {
  return BILL_TAG_CONFIG[billType] || { label: billType || 'Unknown', color: 'default' };
};

const BillHistoryDrawer = ({
  isOpen,
  onClose,
  selectedClient,
  loading: parentLoading,
  showMembershipDetails,
  showPackageDetails,
  toggleMembershipDetails,
  togglePackageDetails
}) => {
  const { fetchBillByClientNumber, activeBranchId } = useMain();

  const [apiLoading, setApiLoading] = useState(false);
  const [allBills, setAllBills] = useState([]);

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const cleanDate = dateString.split('T')[0];
    const parts = cleanDate.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}-${month}-${year}`;
    }
    return dateString;
  };

  useEffect(() => {
    const fetchBills = async () => {
      if (!isOpen || !selectedClient?.clientNumber) return;
      try {
        setApiLoading(true);
        const resp = await fetchBillByClientNumber(
          selectedClient.clientNumber,
          `?branchId=${activeBranchId}`
        );

        if (resp?.statusCode === 200) {
          const serviceProductWalletBills = resp.data?.serviceProductWalletBills || [];
          const membershipPackageBills = resp.data?.membershipPackageBills || [];

          // Normalize serviceProductWalletBills (billType: Bill / Wallet)
          const normalizedServiceProductBills = serviceProductWalletBills.map((bill) => {
            const services = (bill.services || []).map((s) => ({
              serviceName: s.serviceName || s.service?.serviceName,
              category: s.category || s.service?.category,
              amount: s.amount,
              serviceProvider: Array.isArray(s.serviceProvider)
                ? s.serviceProvider[0]
                : s.serviceProvider
            }));

            const products = (bill.products || []).map((p) => ({
              productName: p.productName || p.product?.productName,
              quantity: p.quantity,
              price: p.price,
              amount: p.amount
            }));

            return {
              _id: bill._id,
              invoice: bill.invoice,
              billType: bill.billType, // "Bill" or "Wallet"
              dateOfBilling: bill.dateOfBilling,
              timeOfBilling: bill.timeOfBilling,
              amountPayable: bill.amountPayable,
              paidDues: bill.paidDues,
              amountPaid: bill.amountPaid || [],
              notes: bill.notes,
              noteForClient: bill.noteForClient,
              services,
              products
            };
          });

          // Normalize membershipPackageBills (billType: Membership / Package)
          const normalizedMembershipPackageBills = membershipPackageBills.map((bill) => {
            const memberships = (bill.memberships || []).map((m) => ({
              serviceName: m.membershipName,
              category: 'Membership',
              amount: m.amount,
              serviceProvider: Array.isArray(m.serviceProvider)
                ? m.serviceProvider[0]
                : m.serviceProvider
            }));

            const packages = (bill.packages || []).map((p) => ({
              serviceName: p.packageName,
              category: 'Package',
              amount: p.amount,
              serviceProvider: Array.isArray(p.serviceProvider)
                ? p.serviceProvider[0]
                : p.serviceProvider
            }));

            return {
              _id: bill._id,
              invoice: bill.invoice,
              billType: bill.billType, // "Membership" or "Package"
              dateOfBilling: bill.dateOfBilling,
              timeOfBilling: bill.timeOfBilling,
              amountPayable: bill.amountPayable,
              paidDues: bill.paidDues,
              amountPaid: bill.amountPaid || [],
              notes: bill.notes,
              noteForClient: bill.noteForClient,
              services: [...memberships, ...packages],
              products: []
            };
          });

          const merged = [
            ...normalizedServiceProductBills,
            ...normalizedMembershipPackageBills
          ].sort((a, b) => {
            const dateA = new Date(`${a.dateOfBilling} ${a.timeOfBilling || ''}`);
            const dateB = new Date(`${b.dateOfBilling} ${b.timeOfBilling || ''}`);
            return dateB - dateA; // newest first
          });

          setAllBills(merged);
        } else {
          setAllBills([]);
        }
      } catch (error) {
        console.error('Error fetching client bills:', error);
        setAllBills([]);
      } finally {
        setApiLoading(false);
      }
    };

    fetchBills();
  }, [isOpen, selectedClient?.clientNumber, activeBranchId]);

  const loading = parentLoading || apiLoading;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '750px' },
          boxSizing: 'border-box',
        },
      }}
    >
      {loading ? (
        <ShowLoader />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: '100%', p: 1 }}>
          {/* Client Details Section */}
          <Box sx={{ width: { xs: '100%', sm: '50%' }, pl: 1, pt: { xs: 0, sm: 1 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#6174DD',
                color: 'white',
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                minHeight: '44px',
                mb: 1
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'white', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Client Details
              </Typography>
            </Box>
            <DialogContent sx={{ px: { xs: 1, sm: 1.5 }, py: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                <Typography variant="body1">
                  <strong>Bill Frequency:</strong> {selectedClient?.billsFrequency || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>Remaining Total:</strong> ₹{selectedClient?.remainingTotal || '0'}
                </Typography>
                <Typography variant="body1">
                  <strong>Total Bill Amount Till Date:</strong> ₹{selectedClient?.totalBillsAmount || '0'}
                </Typography>

                {/* Membership Details */}
                <Box>
                  <Typography
                    variant="body1"
                    onClick={toggleMembershipDetails}
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      textDecoration: 'underline',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <strong>Membership Buy:</strong>
                    <Box component="span" sx={{ ml: 1 }}>
                      {selectedClient?.membershipDetails?.some(m => m.startDate && m.endDate) ? 'YES' : 'NO'}
                    </Box>

                  </Typography>
                  <Collapse in={showMembershipDetails}>
                    <Paper elevation={0} sx={{
                      mt: 1,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: 'background.paper'
                    }}>
                      {selectedClient?.membershipDetails?.length > 0 ? (
                        selectedClient.membershipDetails
                          .filter(membership => (
                            (membership.services && membership.services.length > 0) ||
                            (membership.startDate && membership.endDate) ||
                            membership.membershipType
                          ))
                          .map((membership, index) => (
                            <Box key={index} sx={{
                              mb: index < selectedClient.membershipDetails.length - 1 ? 2 : 0,
                              pb: index < selectedClient.membershipDetails.length - 1 ? 2 : 0,
                              borderBottom: index < selectedClient.membershipDetails.length - 1 ? '1px solid #e0e0e0' : 'none'
                            }}>
                              <Typography variant="body2">
                                <strong>Membership Name:</strong> {membership?.membershipId?.membershipName}
                              </Typography>
                              {membership.membershipType === 'discountType' ? (
                                <>
                                  <Typography variant="body2">
                                    <strong>Service Discount:</strong> {membership.discount?.discountOnService || '0'}{membership.discount?.serviceDiscountType || '%'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Product Discount:</strong> {membership.discount?.discountOnProduct || '0'}{membership.discount?.productDiscountType || '%'}
                                  </Typography>
                                </>
                              ) : (
                                <Box>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Services:</strong>
                                  </Typography>
                                  {membership.services?.map((service, serviceIndex) => (
                                    <Typography key={serviceIndex} variant="body2" sx={{ pl: 1 }}>
                                      {service.serviceName} (count: {service.count})
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                              <Typography variant="body2">
                                <strong>Validity:</strong> {formatDate(membership.startDate)} to {formatDate(membership.endDate)}
                              </Typography>
                            </Box>
                          ))
                      ) : (
                        <Typography variant="body2">No membership details available</Typography>
                      )}

                    </Paper>
                  </Collapse>
                </Box>

                {/* Package Details */}
                <Box>
                  <Typography
                    variant="body1"
                    onClick={togglePackageDetails}
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      textDecoration: 'underline',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <strong>Package Buy:</strong>
                    <Box component="span" sx={{ ml: 1 }}>
                      {selectedClient?.packageDetails?.length > 0 ? 'YES' : 'NO'}
                    </Box>
                  </Typography>
                  <Collapse in={showPackageDetails}>
                    <Paper elevation={0} sx={{
                      mt: 1,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: 'background.paper'
                    }}>
                      {selectedClient?.packageDetails?.length > 0 ? (
                        selectedClient.packageDetails.map((pkg, index) => (
                          <Box key={index} sx={{
                            mb: index < selectedClient.packageDetails.length - 1 ? 2 : 0,
                            pb: index < selectedClient.packageDetails.length - 1 ? 2 : 0,
                            borderBottom: index < selectedClient.packageDetails.length - 1 ? '1px solid #e0e0e0' : 'none'
                          }}>
                            <Typography variant="body2">
                              <strong>Name:</strong> {pkg.packageName}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Price:</strong> ₹{pkg.price}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Validity:</strong> {formatDate(pkg.startDate)} to {formatDate(pkg.validity)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Services:</strong> {pkg.services.map(s => s.serviceName).join(', ')}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2">No package details available</Typography>
                      )}
                    </Paper>
                  </Collapse>
                </Box>
              </Box>
            </DialogContent>
          </Box>

          {/* Bill History Section */}
          <Box sx={{
            width: { xs: '100%', sm: '50%' },
            pr: 1,
            overflowY: 'auto',
            maxHeight: '100vh',
            borderLeft: { xs: 'none', sm: '1px solid #e0e0e0' },
            pl: { xs: 0, sm: 1.5 },
            mt: { xs: 2, sm: 0 }
          }}>
            {/* Sticky Header with Close Button */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'background.paper',
                pt: { xs: 0, sm: 1 },
                pb: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#6174DD',
                  color: 'white',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75,
                  minHeight: '44px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                    color: 'white',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mr: 1,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                  title={`Bill History of ${selectedClient?.clientName || ''}`}
                >
                  Bill History of {selectedClient?.clientName}
                </Typography>
                <Button
                  onClick={onClose}
                  variant="contained"
                  size="small"
                  startIcon={<CloseIcon sx={{ fontSize: '18px !important' }} />}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    px: 1.25,
                    py: 0.35,
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    boxShadow: 'none',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.35)',
                      boxShadow: 'none',
                      border: '1px solid rgba(255, 255, 255, 0.7)',
                    },
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>
            <DialogContent sx={{ px: { xs: 0.5, sm: 1 }, py: 1 }}>
              {allBills?.length > 0 ? (
                allBills.map((bill, index) => {
                  const tag = getBillTag(bill.billType);
                  return (
                    <Paper
                      key={bill._id || index}
                      elevation={2}
                      sx={{
                        mb: 2.5,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: 'background.paper'
                      }}
                    >
                      {/* Date & Amount Row */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 0.5
                      }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                        >
                          {bill.dateOfBilling ? formatDate(bill.dateOfBilling) : ''}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 'bold', color: 'primary.main', whiteSpace: 'nowrap' }}
                        >
                          ₹{bill.amountPayable}
                        </Typography>
                      </Box>

                      {/* Invoice & Tag Row */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                      }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          {bill.invoice ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', wordBreak: 'break-all' }}
                            >
                              Invoice: {bill.invoice}
                            </Typography>
                          ) : null}
                        </Box>
                        <Chip
                          label={tag.label}
                          color={tag.color}
                          size="small"
                          variant="filled"
                          sx={{
                            height: 22,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            flexShrink: 0
                          }}
                        />
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      {/* Services Section */}
                      {bill?.services?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Services:
                          </Typography>
                          {bill.services.map((service, serviceIndex) => (
                            <Box
                              key={serviceIndex}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 0.5,
                                pl: 1
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                                  {service.serviceName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {service.category}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2">
                                  ₹{service.amount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {service.serviceProvider?.name}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Products Section */}
                      {bill?.products?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Products:
                          </Typography>
                          {bill.products.map((product, productIndex) => (
                            <Box
                              key={productIndex}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 0.5,
                                pl: 1
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                                  {product.productName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Qty: {product.quantity} × ₹{product.price}
                                </Typography>
                              </Box>
                              <Typography variant="body2">
                                ₹{product.amount}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Divider sx={{ my: 1 }} />

                      {/* Payment Details */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 1
                      }}>
                        <Box>
                          <Typography variant="body2">
                            <strong>Payment:</strong> {bill?.amountPaid?.map(el => `₹${el.amount} (${el.paymentType})`).join(', ')}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color={bill.paidDues > 0 ? 'error' : 'success.main'}>
                          <strong>Due:</strong> ₹{bill.paidDues || '0'}
                        </Typography>
                      </Box>

                      {/* Bill Notes */}
                      {(bill?.notes || bill?.noteForClient) && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          {bill?.notes && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Bill Note:</strong> {bill.notes}
                            </Typography>
                          )}
                          {bill?.noteForClient && (
                            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                              <strong>Client Note for This Bill:</strong> {bill.noteForClient}
                            </Typography>
                          )}
                        </>
                      )}
                    </Paper>
                  );
                })
              ) : (
                <Typography variant="body1" sx={{ textAlign: 'center', mt: 2 }}>
                  No bill history available
                </Typography>
              )}
            </DialogContent>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default BillHistoryDrawer;